import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { getEvents, subscribeToLocation, deleteEvent } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { withSectionProtection } from '../../client/components/ProtectedSection';
import { 
  Search, 
  Filter, 
  Plus, 
  Calendar, 
  Clock,
  MapPin,
  Users,
  User,
  ExternalLink,
  Edit3,
  Trash2,
  Eye,
  UserPlus,
  Music,
  GraduationCap,
  Briefcase,
  Coffee,
  Heart,
  Grid3X3,
  List,
  Table
} from 'lucide-react';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { isAdministrator } from '../../client/lib/platformRole';
import { useResponsiveLayout } from '../../components/ui/enhanced-page-layout';
import { useFixedPageStack } from '../../client/hooks/useFixedPageStack';
import {
  FLAGSHIP_EVENT_TYPE,
  FLAGSHIP_EVENTS_LIST_FILTERS,
  REGULAR_EVENTS_LIST_FILTERS,
} from './eventFilters';
import { eventsTimeRangeFilters, isEventConcluded } from './eventDateRange';

type EventsPageVariant = 'regular' | 'flagship';

type EventType = 'workshop' | 'meeting' | 'cultural' | 'business' | 'social' | 'educational' | 'event' | 'all';

const EVENT_TYPES = [
  { value: 'all', label: 'All Types', icon: Filter },
  { value: 'event', label: 'Event', icon: Calendar },
  { value: 'workshop', label: 'Workshop', icon: GraduationCap },
  { value: 'meeting', label: 'Meeting', icon: Briefcase },
  { value: 'cultural', label: 'Cultural', icon: Music },
  { value: 'business', label: 'Business', icon: Briefcase },
  { value: 'social', label: 'Social', icon: Coffee },
  { value: 'educational', label: 'Educational', icon: GraduationCap }
];

type EventsPageProps = {
  variant?: EventsPageVariant;
};

export function EventsPage({ variant = 'regular' }: EventsPageProps) {
  const isFlagshipPage = variant === 'flagship';
  const { data: user } = useAuth();
  const { selectedLocation, getLocationIdsForQuery } = useLocationContext();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const routerLocation = useLocation();
  const locSlug = resolveLocationSlug(routerLocation.pathname, params.slug, selectedLocation);

  // Delete event action
  const deleteEventAction = useAction(deleteEvent);

  // State for filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventType, setSelectedEventType] = useState<EventType>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'date' | 'title'>('date');
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Use responsive layout hook
  const { viewMode, setViewMode, isMobile, shouldCollapseSidebar, hideViewSwitcher } = useResponsiveLayout('table');

  // State for subscription handling
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  // Fetch events using filtered location IDs
  const locationIdsForQuery = getLocationIdsForQuery();
  const {
    data: events = [],
    isLoading,
    error,
    refetch: refetchEvents
  } = useQuery(getEvents, {
    locationIds: locationIdsForQuery,
    filters: {
      ...(isFlagshipPage ? FLAGSHIP_EVENTS_LIST_FILTERS : REGULAR_EVENTS_LIST_FILTERS),
      ...(!isFlagshipPage
        ? eventsTimeRangeFilters(showPastEvents ? 'includePast' : 'upcoming')
        : {}),
      ...(!isFlagshipPage && selectedEventType !== 'all'
        ? { eventType: selectedEventType }
        : {}),
    },
  }, {
    enabled: locationIdsForQuery.length > 0
  });

  // Handle subscription to location if needed
  useEffect(() => {
    const handleSubscription = async () => {
      if (!user || !selectedLocation?.id || isSubscribing) return;
      
      // Check if the error is due to access denied
      if (error && (error as any).message?.includes('Access denied for this location')) {
        try {
          setIsSubscribing(true);
          setSubscriptionError(null);
          
          await subscribeToLocation({ locationId: selectedLocation.id });
          
          // Refetch events after successful subscription
          await refetchEvents();
        } catch (subError: any) {
          console.error('Subscription failed:', subError);
          setSubscriptionError(subError.message || 'Failed to subscribe to location');
        } finally {
          setIsSubscribing(false);
        }
      }
    };

    handleSubscription();
  }, [error, user, selectedLocation, isSubscribing, refetchEvents]);

  // Filter and sort events
  const filteredAndSortedEvents = useMemo(() => {
    let filtered = events;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((event: any) => 
        event.title.toLowerCase().includes(searchLower) ||
        event.description?.toLowerCase().includes(searchLower) ||
        event.eventType?.toLowerCase().includes(searchLower) ||
        event.venueName?.toLowerCase().includes(searchLower) ||
        event.organizer?.username?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'date':
          return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return sorted;
  }, [events, searchTerm, sortBy]);

  const fixedStack = useFixedPageStack({
    secondaryKind: viewMode === 'table' ? 'ref' : 'none',
    secondaryRefDeps: [viewMode, showFilters, filteredAndSortedEvents.length, isLoading, error],
  });

  // Get icon for event type
  const getEventTypeIcon = (eventType: string | null) => {
    if (eventType === FLAGSHIP_EVENT_TYPE) {
      return <ExternalLink className="w-4 h-4" />;
    }
    const type = EVENT_TYPES.find(t => t.value === eventType);
    if (type && type.icon) {
      const IconComponent = type.icon;
      return <IconComponent className="w-4 h-4" />;
    }
    return <Calendar className="w-4 h-4" />;
  };

  const flagshipEventsPath = locationSectionPath(locSlug, 'events', 'flagship');
  const regularEventsPath = locationSectionPath(locSlug, 'events');

  const concludedBadge = (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
      Concluded
    </span>
  );

  // Format event date and time
  const formatEventDateTime = (startTime: string, endTime: string, timezone: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const sameDay = start.toDateString() === end.toDateString();
    
    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };
    
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };

    if (sameDay) {
      return {
        date: start.toLocaleDateString('en-US', dateOptions),
        time: `${start.toLocaleTimeString('en-US', timeOptions)} - ${end.toLocaleTimeString('en-US', timeOptions)}`
      };
    } else {
      return {
        date: `${start.toLocaleDateString('en-US', dateOptions)} - ${end.toLocaleDateString('en-US', dateOptions)}`,
        time: `${start.toLocaleTimeString('en-US', timeOptions)} - ${end.toLocaleTimeString('en-US', timeOptions)}`
      };
    }
  };

  if (!selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Location Selected</h2>
            <p className="text-gray-600">Please select a location to view events.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className={fixedStack.pageContainerClassName} style={fixedStack.pageContainerStyle}>
        {/* Fixed Header */}
        <div
          ref={fixedStack.pageHeaderRef}
          className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-white transition-all duration-300"
        >
          <div className="px-4 py-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1">
                <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
                  {isFlagshipPage ? 'Flagship Events' : 'Events & Activities'}
                </h1>
                <p className="text-xs text-gray-600 mt-1 sm:text-sm line-clamp-2">
                  {isFlagshipPage
                    ? `Flagship programs at ${selectedLocation?.name || 'All Locations'} (${filteredAndSortedEvents.length} programs)`
                    : `Discover and join events at ${selectedLocation?.name || 'All Locations'} (${filteredAndSortedEvents.length} events)`}
                </p>
              </div>
              <div className="flex w-full min-w-0 items-center justify-end gap-2 overflow-x-auto sm:w-auto sm:overflow-visible">
                {isFlagshipPage ? (
                  <Link
                    to={regularEventsPath}
                    className="flex shrink-0 items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <Calendar className="w-4 h-4 shrink-0" />
                    All Events
                  </Link>
                ) : (
                  <Link
                    to={flagshipEventsPath}
                    className="flex shrink-0 items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <ExternalLink className="w-4 h-4 shrink-0" />
                    <span className="sm:hidden">Flagship</span>
                    <span className="hidden sm:inline">Flagship Events</span>
                  </Link>
                )}
                {!isFlagshipPage && showFilters && (
                  <button
                    type="button"
                    onClick={() => setShowPastEvents((on) => !on)}
                    className={`flex shrink-0 items-center gap-1.5 px-2.5 py-2 sm:gap-2 sm:px-3 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap ${
                      showPastEvents
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Clock className="w-4 h-4 shrink-0" />
                    <span className="hidden sm:inline">Show Past Events</span>
                    <span className="sm:hidden">Past</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowFilters((f) => !f)}
                  className={`flex shrink-0 items-center gap-1.5 px-2.5 py-2 sm:gap-2 sm:px-3 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                  aria-expanded={showFilters}
                >
                  <Filter className="w-4 h-4 shrink-0" />
                  <span>Filters</span>
                </button>
                {!isFlagshipPage && user && (
                  <button
                    type="button"
                    onClick={() => navigate(locationSectionPath(locSlug, 'events', 'add'))}
                    className="flex shrink-0 items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-2 sm:gap-2 sm:px-4 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    <Plus className="w-4 h-4 shrink-0" />
                    <span className="hidden sm:inline">Post Event</span>
                    <span className="sm:hidden">Post</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Table Header - only show for table view */}
        {viewMode === 'table' && (
          <div
            ref={fixedStack.secondaryStackRef}
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-0 lg:left-[var(--sidebar-width)] right-0 z-20 bg-sidebar-hover border-b border-border transition-all duration-300`}
          >
            <div className="px-6 py-3 space-y-3">
              {/* Column Headers */}
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider">
                <div className="col-span-3">Event & Description</div>
                <div className="col-span-1">Type</div>
                <div className="col-span-2">Date & Time</div>
                <div className="col-span-2">Location</div>
                <div className="col-span-2">Organizer</div>
                <div className="col-span-2">Capacity</div>
              </div>
              
              {/* Column Filters */}
              {showFilters && <div className="grid grid-cols-12 gap-4">
                {/* Event & Description Filter */}
                <div className="col-span-3">
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-semibold text-purple-700 placeholder-purple-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                {/* Type Filter */}
                <div className="col-span-1">
                  {isFlagshipPage ? (
                    <div className="w-full px-3 py-2 text-sm font-semibold text-purple-700">Flagship</div>
                  ) : (
                    <select
                      value={selectedEventType}
                      onChange={(e) => setSelectedEventType(e.target.value as EventType)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-semibold text-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                    >
                      <option value="all">All</option>
                      {EVENT_TYPES.filter(type => type.value !== 'all').map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                {/* Date & Time Filter */}
                <div className="col-span-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-semibold text-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                  >
                    <option value="date">By Event Date</option>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="title">Alphabetical</option>
                  </select>
                </div>
                
                {/* Location Filter - Placeholder */}
                <div className="col-span-2">
                  <div className="w-full px-3 py-2 text-sm text-gray-400">All Locations</div>
                </div>
                
                {/* Organizer Filter - Placeholder */}
                <div className="col-span-2">
                  <div className="w-full px-3 py-2 text-sm text-gray-400">All Organizers</div>
                </div>
                
                {/* Capacity Filter - Placeholder */}
                <div className="col-span-2">
                  <div className="w-full px-3 py-2"></div>
                </div>
              </div>}
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div
          className={fixedStack.scrollContainerClassName}
          style={fixedStack.scrollContainerStyle}
        >
          <div className="w-full">
            {showFilters && viewMode !== 'table' && selectedLocation && (
              <div className="px-4 py-3 flex flex-wrap gap-3 items-center border-b border-gray-200 bg-gray-50">
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 min-w-[200px] px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-semibold text-purple-700 placeholder-purple-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {!isFlagshipPage && (
                  <select
                    value={selectedEventType}
                    onChange={(e) => setSelectedEventType(e.target.value as EventType)}
                    className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-semibold text-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All types</option>
                    {EVENT_TYPES.filter(type => type.value !== 'all').map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* No Location Selected */}
            {!selectedLocation && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Location Selected</h3>
                  <p className="text-gray-600">Please select a location to view events.</p>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && selectedLocation && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading events...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && selectedLocation && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {(error as any).message?.includes('Access denied') ? 'Subscription Required' : 'Error Loading Events'}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {(error as any).message?.includes('Access denied') 
                      ? `You need to subscribe to ${selectedLocation?.name} to view events.`
                      : 'Please try again later.'
                    }
                  </p>
                  {isSubscribing && (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="text-sm text-gray-600">Subscribing to location...</span>
                    </div>
                  )}
                  {subscriptionError && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{subscriptionError}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && selectedLocation && filteredAndSortedEvents.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {isFlagshipPage ? 'No Flagship Events Found' : 'No Events Found'}
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm || (!isFlagshipPage && selectedEventType !== 'all') || showPastEvents
                      ? 'Try adjusting your search or filters'
                      : isFlagshipPage
                        ? 'No flagship programs are available for this location yet'
                        : 'Be the first to create an event for this location'}
                  </p>
                </div>
              </div>
            )}

            {/* Content */}
            {!isLoading && !error && selectedLocation && filteredAndSortedEvents.length > 0 && (
              <>
                {/* Table View */}
                {viewMode === 'table' && (
                  <div className="w-full">
                    {/* Table Body - No internal scroll */}
                    <div className="divide-y divide-gray-200">
                      {filteredAndSortedEvents.map((event) => {
                        const dateTime = formatEventDateTime(event.startTime, event.endTime, event.timezone);
                        const isOwner = !!user && (user.id === event.organizerId || isAdministrator(user));
                        const concluded = isFlagshipPage && isEventConcluded(event.endTime);
                        
                        return (
                          <div key={event.id} className={`px-6 py-3 hover:bg-hover transition-colors ${concluded ? 'opacity-80' : ''}`}>
                            <div className="grid grid-cols-12 gap-4 items-start">
                              {/* Event & Description */}
                              <div className="col-span-3">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Link
                                    to={locationSectionPath(locSlug, 'events', event.id)}
                                    className="text-sm font-medium text-gray-900 hover:text-blue-600 line-clamp-2"
                                  >
                                    {event.title}
                                  </Link>
                                  {concluded && concludedBadge}
                                </div>
                                {event.description && (
                                  <div className="text-sm text-gray-500 line-clamp-2 mt-1">
                                    {event.description}
                                  </div>
                                )}
                              </div>
                              
                              {/* Type */}
                              <div className="col-span-1">
                                <div className="flex items-center gap-2">
                                  {getEventTypeIcon(event.eventType)}
                                  <span className="text-sm text-gray-600 capitalize">
                                    {event.eventType || 'Event'}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Date & Time */}
                              <div className="col-span-2">
                                <div className="text-sm text-gray-900">{dateTime.date}</div>
                                <div className="text-sm text-gray-500">{dateTime.time}</div>
                              </div>
                              
                              {/* Location */}
                              <div className="col-span-2">
                                <div className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-900">
                                    {event.isVirtual ? 'Virtual' : (event.venueName || 'TBD')}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Organizer */}
                              <div className="col-span-2">
                                <span className="text-sm text-gray-900">
                                  {event.organizer.username || event.organizer.email}
                                </span>
                              </div>
                              
                              {/* Capacity */}
                              <div className="col-span-1">
                                <span className="text-sm text-gray-600">
                                  {event._count?.attendees || 0}
                                  {event.maxAttendees && `/${event.maxAttendees}`}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Cards View */}
                {viewMode === 'cards' && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {filteredAndSortedEvents.map((event) => {
                        const dateTime = formatEventDateTime(event.startTime, event.endTime, event.timezone);
                        const isOwner = !!user && (user.id === event.organizerId || isAdministrator(user));
                        const concluded = isFlagshipPage && isEventConcluded(event.endTime);
                        
                        return (
                          <div
                            key={event.id}
                            className={`bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow cursor-pointer ${concluded ? 'opacity-80' : ''}`}
                            onClick={() => navigate(locationSectionPath(locSlug, 'events', event.id))}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                {getEventTypeIcon(event.eventType)}
                                <span className="text-sm font-medium text-gray-600 capitalize">
                                  {event.eventType || 'Event'}
                                </span>
                                {concluded && concludedBadge}
                              </div>
                            </div>
                            
                            <h3 className="font-semibold text-gray-900 text-lg mb-2">
                              {event.title}
                            </h3>
                            
                            {event.description && (
                              <p className="text-gray-600 mb-4 line-clamp-3">
                                {event.description}
                              </p>
                            )}
                            
                            <div className="space-y-2 mb-4">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="w-4 h-4" />
                                <span>{dateTime.date}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Clock className="w-4 h-4" />
                                <span>{dateTime.time}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <MapPin className="w-4 h-4" />
                                <span>{event.isVirtual ? 'Virtual' : (event.venueName || 'TBD')}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Users className="w-4 h-4" />
                                <span>
                                  {event._count?.attendees || 0} attending
                                  {event.maxAttendees && ` (${event.maxAttendees} max)`}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <span>Created: {new Date(event.createdAt).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* List View */}
                {viewMode === 'list' && (
                  <div className="p-4">
                    <div className="space-y-3 sm:space-y-4">
                      {filteredAndSortedEvents.map((event) => {
                        const dateTime = formatEventDateTime(event.startTime, event.endTime, event.timezone);
                        const isOwner = !!user && (user.id === event.organizerId || isAdministrator(user));
                        const concluded = isFlagshipPage && isEventConcluded(event.endTime);
                        
                        return (
                          <div
                            key={event.id}
                            className={`bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow cursor-pointer hover:bg-hover transition-colors ${concluded ? 'opacity-80' : ''}`}
                            onClick={() => navigate(locationSectionPath(locSlug, 'events', event.id))}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  {getEventTypeIcon(event.eventType)}
                                  <span className="text-sm font-medium text-gray-600 capitalize">
                                    {event.eventType || 'Event'}
                                  </span>
                                  {concluded && concludedBadge}
                                </div>
                                
                                <div>
                                  <Link
                                    to={locationSectionPath(locSlug, 'events', event.id)}
                                    className="text-lg font-semibold text-gray-900 hover:text-blue-600 mb-2 block"
                                  >
                                    {event.title}
                                  </Link>
                                  
                                  {event.description && (
                                    <p className="text-gray-600 mb-3 line-clamp-2">
                                      {event.description}
                                    </p>
                                  )}

                                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                                    <div className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" />
                                      <span>{dateTime.date}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Clock className="w-4 h-4" />
                                      <span>{dateTime.time}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <MapPin className="w-4 h-4" />
                                      <span>{event.isVirtual ? 'Virtual' : (event.venueName || 'TBD')}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Users className="w-4 h-4" />
                                      <span>
                                        {event._count?.attendees || 0} attending
                                        {event.maxAttendees && ` (${event.maxAttendees} max)`}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span>Created: {new Date(event.createdAt).toLocaleDateString()}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
export default withSectionProtection(EventsPage, 'events');



